import {
  Text,
  GreyGrey10,
  GreyGrey70,
  Primary14SemiBold,
  Primary13Regular,
  Primary13SemiBold,
  Flex,
  EmptyState,
  IconReceiptTax
} from '@sequencehq/core-components'
import { Card, InputSelectControlField } from '@sequencehq/core-components'
import { QuickBooksTaxCodeMapping } from 'Integrations/integrationsConfig/quickBooks/quickbooks.integration.config'
import { Dashboardv99990101CommonModels } from '@sequencehq/api/dashboard/v99990101'
import { FC, ReactNode } from 'react'

interface Props {
  taxCodeDefaultsFieldsConfig: {
    taxExemptTaxCode: {
      value: string
      onChange: (value: string) => void
    }
    taxableTaxCode: {
      value: string
      onChange: (value: string) => void
    }
    reverseChargedTaxCode: {
      value: string
      onChange: (value: string) => void
    }
  }
  taxCodeMappingFieldsConfig: {
    [key: string]: {
      value: QuickBooksTaxCodeMapping
      onChange: (value: QuickBooksTaxCodeMapping) => void
    }
  }
  mappings: Record<string, QuickBooksTaxCodeMapping>
  quickbooksTaxCodes: Dashboardv99990101CommonModels.QuickbooksTaxCode.QuickbooksTaxCode[]
}

type TaxCodeOption = {
  value: string
  label: string
}

export const TaxCodesCard = ({
  taxCodeDefaultsFieldsConfig,
  taxCodeMappingFieldsConfig,
  mappings,
  quickbooksTaxCodes
}: Props) => {
  const quickbooksTaxCodesOptions = quickbooksTaxCodes.map(taxCode => ({
    value: taxCode.id,
    label: taxCode.name ?? ''
  }))

  const defaultTaxCodeFields = [
    {
      label: 'Tax exempt customers',
      testId: 'quickbooksIntegration.taxCodes.default.taxExempt',
      value: taxCodeDefaultsFieldsConfig.taxExemptTaxCode.value,
      onChange: taxCodeDefaultsFieldsConfig.taxExemptTaxCode.onChange
    },
    {
      label: 'Reverse charged customers',
      testId: 'quickbooksIntegration.taxCodes.default.reverseCharged',
      value: taxCodeDefaultsFieldsConfig.reverseChargedTaxCode.value,
      onChange: taxCodeDefaultsFieldsConfig.reverseChargedTaxCode.onChange
    },
    {
      label: 'Fallback tax code',
      testId: 'quickbooksIntegration.taxCodes.default.taxable',
      value: taxCodeDefaultsFieldsConfig.taxableTaxCode.value,
      onChange: taxCodeDefaultsFieldsConfig.taxableTaxCode.onChange
    }
  ]

  const hasMappings = Object.keys(mappings).length > 0

  return (
    <Card>
      <Flex
        data-testid="quickbooksIntegration.taxCodes"
        gap={4}
        flexDirection="column"
      >
        <Flex gap={1} flexDirection="column">
          <Text {...Primary14SemiBold}>Tax codes</Text>
          <Text {...Primary13Regular} color={GreyGrey70}>
            Map Sequence invoice line items to QuickBooks tax codes.
          </Text>
        </Flex>
        <TaxCodeSection
          title={
            hasMappings
              ? 'Map product tax categories to QuickBooks tax codes'
              : undefined
          }
        >
          {hasMappings ? (
            <Flex gap={2} flexDirection="column" width="100%">
              {Object.entries(mappings).map(([taxCategoryId, mapping]) => (
                <TaxCodeRow
                  key={taxCategoryId}
                  label={mapping.taxCategoryName}
                  data-testid={`quickbooksIntegration.taxCodes.${taxCategoryId}`}
                  placeholder="Select a tax code..."
                  options={quickbooksTaxCodesOptions}
                  initialValue={
                    taxCodeMappingFieldsConfig[
                      `taxCodeMappings.${taxCategoryId}`
                    ].value?.taxCodeId
                  }
                  onChange={value =>
                    taxCodeMappingFieldsConfig[
                      `taxCodeMappings.${taxCategoryId}`
                    ].onChange({
                      ...mapping,
                      taxCodeId: value
                    })
                  }
                />
              ))}
            </Flex>
          ) : (
            <EmptyState
              data-testid="quickbooksIntegration.taxCodes.mappings.emptyState"
              icon={IconReceiptTax}
              iconSize="sm"
              title="No tax categories to map"
              body="There are no product tax categories to map to QuickBooks tax codes."
              padding="16px"
            />
          )}
        </TaxCodeSection>
        <TaxCodeSection title="For exempt and reverse charged customers, the below tax codes apply">
          <Flex gap={2} flexDirection="column" width="100%">
            {defaultTaxCodeFields.map(field => (
              <TaxCodeRow
                key={field.testId}
                label={field.label}
                data-testid={field.testId}
                placeholder="Select tax code"
                options={quickbooksTaxCodesOptions}
                initialValue={field.value}
                onChange={field.onChange}
              />
            ))}
          </Flex>
        </TaxCodeSection>
      </Flex>
    </Card>
  )
}

const TaxCodeSection: FC<{ title?: string; children: ReactNode }> = ({
  title,
  children
}) => (
  <Flex
    backgroundColor={GreyGrey10}
    borderRadius={6}
    alignItems="center"
    alignContent="center"
    width="100%"
    p={4}
    gap={2}
  >
    <Flex gap={4} flexDirection="column" width="100%">
      {title && <Text {...Primary13SemiBold}>{title}</Text>}
      {children}
    </Flex>
  </Flex>
)

const TaxCodeRow: FC<{
  label: string
  'data-testid': string
  placeholder: string
  options: TaxCodeOption[]
  initialValue?: string
  onChange: (value: string) => void
}> = ({
  label,
  'data-testid': dataTestId,
  placeholder,
  options,
  initialValue,
  onChange
}) => (
  <Flex
    backgroundColor={GreyGrey10}
    borderRadius={6}
    justifyContent="space-between"
    alignContent="center"
    alignItems="center"
    width="100%"
    py={1}
    gap={4}
  >
    <Text isTruncated {...Primary13Regular}>
      {label}
    </Text>
    <InputSelectControlField
      width="180px"
      data-testid={dataTestId}
      placeholder={placeholder}
      options={options}
      initialValue={initialValue}
      onChange={onChange}
    />
  </Flex>
)
